import { jsx as _jsx } from "react/jsx-runtime";
import { PrimitiveErrorBoundary } from '../lib/error-boundary.js';
import { BarChartPrimitive, BarChartPropsSchema } from './bar-chart.js';
import { CalloutPrimitive, CalloutPropsSchema } from './callout.js';
import { ItemListPrimitive, ItemListPropsSchema } from './item-list.js';
import { KeyValuePrimitive, KeyValuePropsSchema } from './key-value.js';
import { LinkListPrimitive, LinkListPropsSchema } from './link-list.js';
import { MarkdownPrimitive, MarkdownPropsSchema } from './markdown.js';
import { MetricGridPrimitive, MetricGridPropsSchema } from './metric-grid.js';
import { QuotePrimitive, QuotePropsSchema } from './quote.js';
import { StackedBarChartPrimitive, StackedBarChartPropsSchema } from './stacked-bar-chart.js';
import { TablePrimitive, TablePropsSchema } from './table.js';
import { TimelinePrimitive, TimelinePropsSchema } from './timeline.js';
/**
 * The 11 non-escape-hatch block primitives, keyed by their `Block['type']`
 * literal from `@almanac/contracts`. "Non-escape-hatch" mirrors the
 * contracts comment on `SimpleBlockSchema`: these are the only block types
 * that may appear as a `diagram` / `unsupported_visualization_request`
 * `fallback_block`, which keeps fallback rendering non-recursive.
 */
export const SIMPLE_PRIMITIVES = {
    markdown: {
        propsSchema: MarkdownPropsSchema,
        description: 'Freeform sanitized GitHub-flavored markdown. Use for prose.',
        Component: MarkdownPrimitive,
    },
    item_list: {
        propsSchema: ItemListPropsSchema,
        description: 'A list of feedback-capable items with title/detail/links.',
        Component: ItemListPrimitive,
    },
    callout: {
        propsSchema: CalloutPropsSchema,
        description: 'A highlighted banner (info/warn/success/danger) with an optional link.',
        Component: CalloutPrimitive,
    },
    metric_grid: {
        propsSchema: MetricGridPropsSchema,
        description: 'A grid of labeled KPI values, optionally colored by state.',
        Component: MetricGridPrimitive,
    },
    table: {
        propsSchema: TablePropsSchema,
        description: 'A simple text table with columns and rows.',
        Component: TablePrimitive,
    },
    timeline: {
        propsSchema: TimelinePropsSchema,
        description: 'A vertical list of time-ordered entries.',
        Component: TimelinePrimitive,
    },
    link_list: {
        propsSchema: LinkListPropsSchema,
        description: 'A list of labeled links (http/https/mailto only).',
        Component: LinkListPrimitive,
    },
    key_value: {
        propsSchema: KeyValuePropsSchema,
        description: 'A list of label/value pairs.',
        Component: KeyValuePrimitive,
    },
    quote: {
        propsSchema: QuotePropsSchema,
        description: 'A pull-quote with an optional citation.',
        Component: QuotePrimitive,
    },
    bar_chart: {
        propsSchema: BarChartPropsSchema,
        description: 'Horizontal percent bars, optionally colored by state.',
        Component: BarChartPrimitive,
    },
    stacked_bar_chart: {
        propsSchema: StackedBarChartPropsSchema,
        description: 'Horizontal bars segmented by series, with an optional legend.',
        Component: StackedBarChartPrimitive,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    },
};
/**
 * Render a non-escape-hatch block. Used both directly by {@link DigestView}
 * and internally by the `diagram` / `unsupported_visualization_request`
 * primitives to render their `fallback_block`.
 */
export function renderSimpleBlock(block, overlay) {
    const entry = SIMPLE_PRIMITIVES[block.type];
    const { type: _type, ...props } = block;
    const Component = entry.Component;
    return (_jsx(PrimitiveErrorBoundary, { label: block.type, children: _jsx(Component, { ...props, overlay: overlay }) }));
}
